import React from "react";
import { useLocation } from "react-router-dom";
import Toggle from "../Toggle";
import Icon from "../btn/Icon";
import MenuItem from "./Item";
import GroupMenuList from "./GroupMenuList";
import StorageMenuList from "./StorageMenuList";
import User from "./User";
import MenuPopover from "./MenuPopover";
import { ChatLine, Document, PlusFill, AiLine } from "../../assets/svgs/icons";
import { Mode } from "../../views/tutorV2/types";
import useMenuView from "../../views/tutorV2/hooks/presentation/useMenuView";
import { Dialog } from "radix-ui";
import { useMediaMd } from "../../hooks/useMedia";
import { useUIStore } from "../../views/tutorV2/store";
import mainLeftBannerImage from "../../assets/images/main_left_banner_air_quiz.jpg";
import pressCardBannerActive from "../../assets/images/main_left_banner_press_card_active.png";
import pressCardBannerIssue from "../../assets/images/main_left_banner_press_card_issue.png";
import pressCardBannerActiveMobile from "../../assets/images/main_left_banner_press_card_active_mobile.png";
import pressCardBannerIssueMobile from "../../assets/images/main_left_banner_press_card_issue_mobile.png";

export interface MenuProps {
  mode: Mode;
}

const Menu: React.FC<MenuProps> = ({ mode = Mode.TUTOR }) => {
  const isMd = useMediaMd();
  const { openNav } = useMenuView({ mode });

  if (!isMd) return <MobileMenu mode={mode} />;

  return openNav ? <ExpandedMenu mode={mode} /> : <CollapsedMenu mode={mode} />;
};

const CollapsedMenu = ({ mode }) => {
  const {
    user,
    openUserPopover,
    setOpenUserPopover,
    handleNewChat,
    toggleNav,
    handlePurchaseClick,
    fetching,
  } = useMenuView({ mode });
  const { setOpenGroup, setOpenStorage } = useUIStore();

  const handleChatClick = () => {
    toggleNav();
    setOpenGroup(true);
    setOpenStorage(false);
  };

  const handleStorageClick = () => {
    toggleNav();
    setOpenStorage(true);
    setOpenGroup(false);
  };

  return (
    <div className="bg-white hidden md:flex flex-col h-[100%] items-center w-16 relative">
      {/* Header Button */}
      <div className="flex items-center justify-center shrink-0 size-16">
        <Icon size="x-lg" style="ghost" icon="Sidebar" onClick={toggleNav} />
      </div>

      {/* Menu Items */}
      <div className="flex-1 flex flex-col items-center w-full">
        <div className="box-border flex gap-[10px] items-center justify-center p-[18px] shrink-0 size-[60px]">
          <PlusFill
            width={24}
            height={24}
            className={
              fetching
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer hover:opacity-80"
            }
            onClick={fetching ? undefined : handleNewChat}
            fill="#6d2ef2"
          />
        </div>
        <div className="box-border flex gap-[10px] items-center justify-center p-3 shrink-0 size-12">
          {mode === Mode.SOCRATES ? (
            <AiLine
              width={24}
              height={24}
              className="cursor-pointer hover:opacity-80"
              onClick={handleChatClick}
            />
          ) : (
            <ChatLine
              width={24}
              height={24}
              className="cursor-pointer hover:opacity-80"
              onClick={handleChatClick}
            />
          )}
        </div>
        {/* 보관함 아이콘 (튜터만 표시) */}
        {mode === Mode.TUTOR && (
          <div className="box-border flex gap-[10px] items-center justify-center p-3 shrink-0 size-12">
            <Document
              width={24}
              height={24}
              className="text-[#212121] cursor-pointer hover:opacity-80"
              onClick={handleStorageClick}
            />
          </div>
        )}
      </div>

      {/* Bottom section */}
      <MenuPopover
        variant="collapsed"
        open={openUserPopover}
        onOpenChange={setOpenUserPopover}
        user={user}
        onPurchaseClick={handlePurchaseClick}
        onItemClick={(item) => console.log("Menu item clicked:", item)}
      />

      {/* Divider line */}
      <div className="absolute right-0 top-0 h-[100%] w-px bg-[#eeeeee]" />
    </div>
  );
};

const ExpandedMenu = ({ mode }: MenuProps) => {
  const location = useLocation();
  const {
    user,
    isDialogMenu,
    openUserPopover,
    userState,
    setOpenUserPopover,
    handleNewChat,
    handleToggleMode,
    handleClickMore,
    toggleNav,
    handlePurchaseClick,
    fetching,
    isPressCardValid,
    handlePressCardClick,
  } = useMenuView({ mode });
  const { setOpenGroup, setOpenStorage, openGroup, openStorage } = useUIStore();
  const showAirMainBanner =
    mode === Mode.TUTOR &&
    (location.pathname === "/" || location.pathname.startsWith("/tutor"));

  const handleChatClick = () => {
    setOpenGroup(!openGroup);
    setOpenStorage(false);
  };

  const handleStorageClick = () => {
    setOpenStorage(!openStorage);
    setOpenGroup(false);
  };

  return (
    <div className="bg-white flex flex-col items-center h-full w-[320px] relative overflow-x-hidden">
      {/* Header */}
      <div className="box-border flex items-center justify-between pl-4 pr-0 py-0 md:py-[2px] w-full">
        {/* Toggle */}
        <div className="bg-[#f7f7f7] border border-[#eeeeee] box-border flex items-center justify-center rounded-[40px]">
          <Toggle
            size="sm"
            isLeft={mode === Mode.TUTOR}
            onClick={handleToggleMode}
            leftValue="tutor"
            leftLabel="튜터"
            rightValue="socrates"
            rightLabel="소크라테스"
          />
        </div>

        {/* Icon Button - responsive size via Tailwind */}
        <Icon
          size="lg"
          style="ghost"
          icon={isDialogMenu ? "Close" : "Sidebar"}
          onClick={toggleNav}
          className="md:w-12 md:h-12"
        />
      </div>

      {/* Menu Lists */}
      <div className="flex-1 flex flex-col items-start w-full overflow-y-auto">
        {/* New Chat Button */}
        <MenuItem
          type="btn"
          label={mode === Mode.TUTOR ? "새 튜터" : "새 소크라테스"}
          onClick={handleNewChat}
          disabled={fetching}
        />

        {/* 그룹 목록 (튜터 목록 / 소크라테스 목록) */}
        <GroupMenuList mode={mode} />

        {/* 보관함 목록 (튜터 보관함만 표시, 소크라테스 보관함 미구현) */}
        {mode === Mode.TUTOR && <StorageMenuList mode={mode} />}
      </div>

      {showAirMainBanner && (
        <div className="hidden md:block w-full shrink-0">
          <a
            href="https://smore.im/form/5dzNgB6J41"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AiR 퀴즈 챌린지"
            className="block w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#6d2ef2]"
          >
            <img
              src={mainLeftBannerImage}
              alt=""
              className="block h-[100px] w-full object-cover"
              draggable={false}
            />
          </a>
        </div>
      )}

      {/* 어린이 기자단 기자증 배너 (#11459, #11205 연계) — 모든 유저 상시 노출 */}
      {/* 발급 완료+유효(isPressCardValid): "나의 기자증 확인하기" → 팝플 기자증 페이지 */}
      {/* 그 외                          : "기자단 혜택 받기"     → 팝플 메인 */}
      <button
        type="button"
        onClick={handlePressCardClick}
        aria-label={
          isPressCardValid ? "나의 기자증 확인하기" : "기자단 혜택 받기"
        }
        className="block w-full shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#6d2ef2]"
      >
        {/* 데스크탑: 부제 포함 100px 배너 / 모바일: 부제 없는 64px 배너 (#11459 피드백) */}
        <img
          src={isPressCardValid ? pressCardBannerActive : pressCardBannerIssue}
          alt=""
          className="hidden md:block h-[100px] w-full object-cover"
          draggable={false}
        />
        <img
          src={
            isPressCardValid
              ? pressCardBannerActiveMobile
              : pressCardBannerIssueMobile
          }
          alt=""
          className="block md:hidden h-[64px] w-full object-cover"
          draggable={false}
        />
      </button>

      {/* User Section - mobile only */}
      <div className="md:hidden w-full">
        <User
          state={userState}
          email={user?.name}
          avatarUrl={""}
          endDate={user?.end_dt}
          onMoreClick={handleClickMore}
          onPurchaseClick={handlePurchaseClick}
        />
      </div>

      {/* Menu Popover - desktop only */}
      <div className="hidden md:block w-full">
        <MenuPopover
          variant="expanded"
          open={openUserPopover}
          onOpenChange={setOpenUserPopover}
          user={user}
          onPurchaseClick={handlePurchaseClick}
          onItemClick={(item) => console.log("Menu item clicked:", item)}
        />
      </div>

      {/* Divider line - desktop only */}
      <div className="hidden md:block absolute right-0 top-0 h-full w-px bg-[#eeeeee]" />
    </div>
  );
};

const MobileMenu = ({ mode }: MenuProps) => {
  const { openNav, toggleNav } = useMenuView({ mode });
  const [bannerHeight, setBannerHeight] = React.useState(0);

  // 오버레이 클릭 시 메뉴 닫기
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      toggleNav();
    }
  };

  React.useEffect(() => {
    const checkBanner = () => {
      const banner = document.querySelector(".ui-top-banner");
      const headerTop = document.querySelector(".common-header-top");
      let totalHeight = 0;

      if (banner) {
        totalHeight += banner.getBoundingClientRect().height;
      }

      if (headerTop) {
        totalHeight += headerTop.getBoundingClientRect().height;
      }

      setBannerHeight(totalHeight);
    };
    checkBanner();

    // MutationObserver로 HeaderBanner 추가/제거 감지
    const observer = new MutationObserver(checkBanner);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [openNav]);

  return (
    <Dialog.Root open={openNav} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed left-0 right-0 bottom-0 bg-black/40 z-50"
          style={{ top: bannerHeight }}
        />
        <Dialog.Title>menu</Dialog.Title>
        <Dialog.Content
          className="bg-white overflow-hidden w-90 absolute left-0 z-50"
          style={{
            top: bannerHeight,
            height: `calc(100dvh - ${bannerHeight}px)`,
          }}
        >
          <ExpandedMenu mode={mode} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Menu;
